/**
 * 动态词汇管理相关类型定义
 */

export interface DynamicLexemeStats {
  total_dynamic_lexemes: number
  pending_review_count: number
  approved_count: number
  rejected_count: number
  top_encounter_words: TopEncounterWord[]
  daily_creation_trend: DailyCreationTrend[]
}

export interface TopEncounterWord {
  word: string
  language: string
  encounter_count: number
  user_count: number
}

export interface DailyCreationTrend {
  date: string
  count: number
}

export interface DynamicLexemeMetadata {
  source: DynamicLexemeSource
  status: DynamicLexemeStatus
  created_by_user?: number
  created_from_context?: string
  encounter_count: number
  user_count: number
  language_detection?: LanguageDetectionResult
  auto_generated_pos?: string
  frequency_estimate?: number
  reviewed_by?: number
  review_notes?: string
  reviewed_at?: string
  confidence_score: number
  needs_pronunciation: boolean
  needs_definition: boolean
}

export interface DynamicLexeme {
  lexeme_id: number
  word: string
  language: string
  status: DynamicLexemeStatus
  is_newly_created: boolean
  metadata: DynamicLexemeMetadata
}

export interface LanguageDetectionResult {
  detected_language: string
  confidence: number
  alternative_languages: string[]
}

export enum DynamicLexemeSource {
  USER_GENERATED = 'user_generated',
  LLM_GENERATED = 'llm_generated',
  MANUAL_IMPORT = 'manual_import',
  API_IMPORT = 'api_import'
}

export enum DynamicLexemeStatus {
  PENDING_REVIEW = 'pending_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  AUTO_APPROVED = 'auto_approved'
}

export interface ReviewRequest {
  lexeme_ids: number[]
  action: DynamicLexemeStatus
  review_notes?: string
  batch_action: boolean
}

export interface SearchParams {
  query?: string
  status?: DynamicLexemeStatus
  // 后端 /search 接口的状态参数名
  status_filter?: DynamicLexemeStatus
  language?: string
  min_encounters?: number
  page?: number
  page_size?: number
}

// /search 接口返回的扁平结构（与 DynamicLexeme 不同，统计字段不在 metadata 里）
export interface DynamicLexemeSearchItem {
  lexeme_id: number
  word: string
  language: string
  pos?: string | null
  status: DynamicLexemeStatus | string
  encounter_count: number
  user_count: number
  created_from_context?: string | null
  created_at?: string | null
  confidence_score: number
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  page_size: number
  has_next: boolean
  has_prev: boolean
}

// 语言配置
export const SUPPORTED_LANGUAGES = {
  'en': '英语',
  'zh': '中文',
  'es': '西班牙语',
  'fr': '法语',
  'de': '德语',
  'ja': '日语',
  'ko': '韩语'
} as const

export const LANGUAGE_COLORS = {
  'en': 'blue',
  'zh': 'red', 
  'es': 'orange',
  'fr': 'purple',
  'de': 'green',
  'ja': 'pink',
  'ko': 'cyan'
} as const

// 状态配置
export const STATUS_CONFIG = {
  [DynamicLexemeStatus.PENDING_REVIEW]: {
    color: 'orange',
    label: '待审核'
  },
  [DynamicLexemeStatus.APPROVED]: {
    color: 'green',
    label: '已批准'
  },
  [DynamicLexemeStatus.REJECTED]: {
    color: 'red',
    label: '已拒绝'
  },
  [DynamicLexemeStatus.AUTO_APPROVED]: {
    color: 'blue',
    label: '自动批准'
  }
} as const